/**
 * The names Review & Run shows for the segments a study is about to run against.
 *
 * The tester read `1 segment selected` where the segment's name belongs — the one line on that page that
 * makes a study auditable before money is spent. Two separate causes, and fixing the first hid the second:
 *
 * 1. the request went to `/audiences/{id}/segments` (PLURAL), which 404s. A 404 does not throw, so the
 *    `catch` never ran either: nothing was set and the page fell back to its bare count. Fixed in FE #709.
 * 2. the id was read from React state inside a callback created on an EARLIER render. On the single-segment
 *    auto-skip path (`App.handleSelectAudience` → `handleSegmentSelect` within the same tick as
 *    `setSelectedAudienceId`) that state is still null, so the function returned before it asked. Every
 *    summit attendee takes that path, which is why the fix looked like it had not shipped.
 *
 * Extracted here so the id is an ARGUMENT. A caller cannot ask without one, `fetch` is injectable, and each
 * way of ending up with no name is one assertion instead of an untestable branch inside a 1,100-line router
 * component.
 *
 * The other rule this file carries: a non-empty selection NEVER resolves to an empty list. An empty list is
 * precisely what made Review print "1 segment selected", so every failure produces an identifying label the
 * user can quote instead.
 */

/** Shown when a segment's real name cannot be resolved. Identifies the run; never pretends to be a name. */
export const segmentFallbackLabel = (segmentId: number): string => `Segment ${segmentId}`;

interface NamedSegment {
  id: number;
  name?: string;
}

/** `/audience/{id}/segments` answers with a bare array on the FastAPI path and `{segments: [...]}` on the
 *  legacy one. Both are in service, so both are read. */
const readSegments = (body: unknown): NamedSegment[] => {
  if (Array.isArray(body)) return body as NamedSegment[];
  if (
    body &&
    typeof body === 'object' &&
    Array.isArray((body as { segments?: unknown }).segments)
  ) {
    return (body as { segments: NamedSegment[] }).segments;
  }
  return [];
};

/**
 * Resolve display names for `segmentIds` within `audienceId`.
 *
 * Matched segments come back in the audience's own order (which is how the list is presented everywhere
 * else); ids the audience does not list are appended as fallback labels, so a segment deleted between
 * selection and review costs one name rather than all of them.
 */
export const resolveSegmentNames = async (
  audienceId: number | null,
  segmentIds: number[],
  fetchImpl: typeof fetch = fetch,
  apiUrl: string = import.meta.env.VITE_API_URL || '',
): Promise<string[]> => {
  if (segmentIds.length === 0) return [];
  // No id, no request — but still an answer. The old early `return` left Review with its count.
  if (audienceId === null || audienceId === undefined) return segmentIds.map(segmentFallbackLabel);

  let segments: NamedSegment[] = [];
  try {
    // SINGULAR. Measured against the running backend: `/audience/{id}/segments` 200, `/audiences/...` 404.
    const response = await fetchImpl(`${apiUrl}/audience/${audienceId}/segments`, {
      credentials: 'include',
    });
    // A non-ok response is not an exception, so it is handled here rather than left to the catch.
    if (response.ok) segments = readSegments(await response.json());
  } catch {
    /* offline, aborted, or a non-JSON body — fall through to the labels below */
  }

  const selected = new Set(segmentIds);
  const named = segments.filter((segment) => selected.has(segment.id));
  const resolved = named.map((segment) => segment.name || segmentFallbackLabel(segment.id));
  const namedIds = new Set(named.map((segment) => segment.id));

  return [...resolved, ...segmentIds.filter((id) => !namedIds.has(id)).map(segmentFallbackLabel)];
};
